import { useEffect, useState } from 'react';
import { disclaimersAPI } from '../services/api';

/**
 * useDisclaimer: text for one disclaimer column (Admin > Disclaimers),
 * resolved server-side for the viewer's brand.
 *
 * Returns { text, error, loading }. Errors are returned, never replaced with
 * fallback copy: render them so a missing disclaimer is visible.
 *
 * Requests are de-duplicated and cached for the page session per
 * (key, portfolioCode, brandDomain).
 */
const cache = new Map();

const cacheKey = (key, portfolioCode, brandDomain) => `${key}|${portfolioCode || ''}|${brandDomain || ''}`;

export const fetchDisclaimer = (key, { portfolioCode, brandDomain } = {}) => {
  const k = cacheKey(key, portfolioCode, brandDomain);
  if (!cache.has(k)) {
    const p = disclaimersAPI.get(key, { portfolioCode, brandDomain })
      .then((r) => r.text)
      .catch((err) => {
        cache.delete(k); // allow a retry on the next mount
        const d = err?.response?.data?.detail;
        throw new Error(typeof d === 'string' ? d : `Disclaimer "${key}" could not be loaded`);
      });
    cache.set(k, p);
  }
  return cache.get(k);
};

export default function useDisclaimer(key, { portfolioCode, brandDomain } = {}) {
  const [state, setState] = useState({ text: null, error: null, loading: true });

  useEffect(() => {
    let cancelled = false;
    setState({ text: null, error: null, loading: true });
    fetchDisclaimer(key, { portfolioCode, brandDomain })
      .then((text) => { if (!cancelled) setState({ text, error: null, loading: false }); })
      .catch((err) => { if (!cancelled) setState({ text: null, error: err.message, loading: false }); });
    return () => { cancelled = true; };
  }, [key, portfolioCode, brandDomain]);

  return state;
}

/** Plain text for exports (Excel cells, server print payloads): tags removed,
 *  paragraphs kept as blank lines. */
export const toPlainText = (text) => {
  if (!text) return '';
  if (!/<[a-z][\s\S]*>/i.test(text)) return text;
  const el = document.createElement('div');
  el.innerHTML = text.replace(/<\/(p|h\d|li)>/gi, '</$1>\n\n').replace(/<br\s*\/?>/gi, '\n');
  return (el.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
};

/** Plain text of a disclaimer; a failure comes back as a visible [error] line
 *  rather than fallback copy, so an export never silently omits it. */
export const fetchDisclaimerPlain = (key, opts) =>
  fetchDisclaimer(key, opts).then(toPlainText).catch((err) => `[${err.message}]`);

/** Plain text from a useDisclaimer() state, for exports built from component state. */
export const disclaimerStateText = ({ text, error, loading }, key) => {
  if (error) return `[${error}]`;
  if (loading) return `[Disclaimer "${key}" still loading]`;
  return toPlainText(text);
};
